import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { saveAs } from 'file-saver';
import { CodaltComponent } from '../../codalt.component';
import { ApiService } from '../../services/api/api.service';
import { Utils } from '../../utils.class';

export interface PaperTypeOption {
    key: string;
    label: string;
    name?: string;
    marginTop?: number | null;
    marginBottom?: number | null;
    marginLeft?: number | null;
    marginRight?: number | null;
}

// In-memory cache zodat opties direct synchroon beschikbaar zijn bij openen
let cachedPaperTypes: PaperTypeOption[] = [];

@Component({
    selector: 'app-pdf-background-dialog',
    templateUrl: './pdf-background-dialog.component.html',
    styleUrls: ['./pdf-background-dialog.component.scss'],
    standalone: false,
})
export class PdfBackgroundDialogComponent extends CodaltComponent implements OnInit {
    selectedFile: File | null = null;
    selectedPaperType = new FormControl<string>('');
    isProcessing: boolean = false;
    errorMessage: string | null = null;
    successMessage: string | null = null;

    paperTypes: PaperTypeOption[] = [];

    readonly defaultMarginTop: number = 3.5;
    readonly defaultMarginBottom: number = 2.7;
    readonly defaultMarginLeft: number = 2.0;
    readonly defaultMarginRight: number = 2.0;

    topMarginBandHeight = 0;
    bottomMarginBandHeight = 0;
    leftMarginBandWidth = 0;
    rightMarginBandWidth = 0;

    constructor(
        public dialogRef: MatDialogRef<PdfBackgroundDialogComponent>,
        private apiService: ApiService
    ) {
        super();
        this.subscriptions.add(this.selectedPaperType.valueChanges.subscribe(() => this.updateMarginBands()));
        this.updateMarginBands();
        this.initializeFromCache();
    }

    ngOnInit(): void {
        this.loadPdfStyles();
    }

    private initializeFromCache(): void {
        if (cachedPaperTypes && cachedPaperTypes.length > 0) {
            this.paperTypes = [...cachedPaperTypes];
            if (!this.selectedPaperType.value && this.paperTypes.length > 0) {
                this.selectedPaperType.setValue(this.paperTypes[0].key);
            }
            return;
        }

        try {
            const raw = localStorage.getItem('cached_pdf_styles');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    cachedPaperTypes = parsed;
                    this.paperTypes = [...parsed];
                    if (!this.selectedPaperType.value && this.paperTypes.length > 0) {
                        this.selectedPaperType.setValue(this.paperTypes[0].key);
                    }
                }
            }
        } catch {
            // Geen cache beschikbaar
        }
    }

    private loadPdfStyles(): void {
        this.apiService
            .getCall<any[]>('pdf-styles')
            .then(response => {
                const styles = Array.isArray(response) ? response : (response as any)?.data || [];
                if (styles && styles.length > 0) {
                    const loadedOptions: PaperTypeOption[] = [];
                    for (const style of styles) {
                        loadedOptions.push({
                            key: style.key,
                            label: style.name ? `Huisstijl: ${style.name}` : `Huisstijl: ${style.key}`,
                            name: style.name,
                            marginTop: style.margin_top != null ? Number(style.margin_top) : null,
                            marginBottom: style.margin_bottom != null ? Number(style.margin_bottom) : null,
                            marginLeft: style.margin_left != null ? Number(style.margin_left) : null,
                            marginRight: style.margin_right != null ? Number(style.margin_right) : null,
                        });
                    }

                    this.paperTypes = loadedOptions;
                    cachedPaperTypes = loadedOptions;

                    try {
                        localStorage.setItem('cached_pdf_styles', JSON.stringify(loadedOptions));
                    } catch {
                        // Ignore
                    }

                    const defaultStyle = styles.find((s: any) => s.is_default) || styles[0];
                    if (
                        defaultStyle &&
                        (!this.selectedPaperType.value ||
                            !this.paperTypes.some(p => p.key === this.selectedPaperType.value))
                    ) {
                        this.selectedPaperType.setValue(defaultStyle.key);
                    }
                }
            })
            .catch(() => {
                // Stille fallback
            });
    }

    get selectedOption(): PaperTypeOption | undefined {
        return this.paperTypes.find(p => p.key === this.selectedPaperType.value);
    }

    private updateMarginBands(): void {
        this.topMarginBandHeight = Math.max(
            26,
            Math.min(65, Math.round((this.selectedOption?.marginTop ?? this.defaultMarginTop) * 12))
        );
        this.bottomMarginBandHeight = Math.max(
            24,
            Math.min(60, Math.round((this.selectedOption?.marginBottom ?? this.defaultMarginBottom) * 12))
        );
        this.leftMarginBandWidth = Math.max(
            22,
            Math.min(55, Math.round((this.selectedOption?.marginLeft ?? this.defaultMarginLeft) * 14))
        );
        this.rightMarginBandWidth = Math.max(
            22,
            Math.min(55, Math.round((this.selectedOption?.marginRight ?? this.defaultMarginRight) * 14))
        );
    }

    onFileInputChange(event: Event): void {
        this.errorMessage = null;
        this.successMessage = null;

        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                this.errorMessage = 'Selecteer een geldig PDF-bestand (.pdf).';
                this.selectedFile = null;
                return;
            }

            const maxSizeBytes = 50 * 1024 * 1024; // 50MB
            if (file.size > maxSizeBytes) {
                this.errorMessage = 'Het geselecteerde bestand is te groot (maximaal 50 MB).';
                this.selectedFile = null;
                return;
            }

            this.selectedFile = file;
        } else {
            this.selectedFile = null;
        }
    }

    applyBackground(): void {
        if (!this.selectedFile) {
            this.errorMessage = 'Selecteer eerst een PDF-bestand om te uploaden.';
            return;
        }

        this.isProcessing = true;
        this.selectedPaperType.disable();
        this.errorMessage = null;
        this.successMessage = null;

        const formData = new FormData();
        formData.append('upload', this.selectedFile, this.selectedFile.name);
        formData.append('paper_type', this.selectedPaperType.value ?? '');

        this.apiService
            .postBlobCall<Blob>('pdf/apply-background', formData)
            .then(blob => {
                this.isProcessing = false;
                this.selectedPaperType.enable();
                const originalName = this.selectedFile?.name.replace(/\.[^/.]+$/, '') || 'document';
                const downloadFileName = `${originalName}_${Utils.formatDate(new Date(), 'ddMMMyyyy_HHmm').replace('.', '')}.pdf`;
                saveAs(blob, downloadFileName);
                this.successMessage = `Briefpapier succesvol toegevoegd! Het bestand "${downloadFileName}" is gedownload.`;
            })
            .catch((response: any) => {
                this.isProcessing = false;
                this.selectedPaperType.enable();
                const err = response?.error;
                if (err?.error instanceof Blob) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const parsed = JSON.parse(reader.result as string);
                            this.errorMessage =
                                parsed.message ||
                                parsed.error ||
                                'Er is een fout opgetreden bij het verwerken van het briefpapier.';
                        } catch {
                            this.errorMessage = 'Er is een fout opgetreden bij het verwerken van de PDF.';
                        }
                    };
                    reader.readAsText(err.error);
                } else {
                    this.errorMessage = err?.message || 'Er is een fout opgetreden bij het verwerken van de PDF.';
                }
            });
    }

    close(): void {
        this.dialogRef.close();
    }
}
